import React, { useEffect, useMemo, useState } from "react";

import CommonModal from "../../../../Components/Modal";
import { useAppDispatch, useAppSelector } from "../../../../Store/Hooks";
import {
  fetchAccountTeam,
  fetchCustomerTeam,
  fetchExecutiveTeamDetail,
  fetchStakeholderRelationships,
  saveStakeholderRelationships,
} from "../../../../Store/Reducers/AccountDetailsSlice";
import {
  normalizeRoleName,
  ROLE_DISPLAY_NAMES,
} from "../../../../Utils/Constants/RoleConstants";
import { toast } from "../../../../Utils/Helper/toast";
import RelationshipMatrix, {
  type VendorStakeholder,
} from "../RelationshipMatrix";

type StakeholdersMappingCardProps = {
  accountId: string;
  className?: string;
};

const INNER_CARD_CLASS =
  "box-border flex h-full min-h-0 flex-col justify-center rounded-lg border border-[#E8EAEB] px-3 py-3";

const StakeholdersMappingCard: React.FC<StakeholdersMappingCardProps> = ({
  accountId,
  className = "",
}) => {
  const dispatch = useAppDispatch();
  const [isMatrixModalOpen, setIsMatrixModalOpen] = useState(false);
  const { accountTeam, customerTeam, executiveTeam, stakeholderRelationships } =
    useAppSelector(state => state.accountDetails);

  useEffect(() => {
    if (!accountId) return;
    dispatch(fetchAccountTeam(accountId));
    dispatch(fetchCustomerTeam(accountId));
    dispatch(fetchExecutiveTeamDetail(accountId));
    dispatch(fetchStakeholderRelationships(accountId));
  }, [accountId, dispatch]);

  const vendorStakeholders = useMemo<VendorStakeholder[]>(() => {
    const accountStakeholders: VendorStakeholder[] = accountTeam.members.map(
      member => ({
        id: member.user_id,
        name: member.full_name || member.email || "Unknown",
        title: member.position || "",
        roleLabel: member.role_name || "",
        source: "account" as const,
      })
    );

    const executiveStakeholders: VendorStakeholder[] = [
      ...executiveTeam.sponsors,
      ...executiveTeam.extraSponsors,
    ]
      .map(member => {
        const normalizedRole = normalizeRoleName(member.role_name || "");
        const displayRole =
          (normalizedRole &&
            ROLE_DISPLAY_NAMES[
              normalizedRole as keyof typeof ROLE_DISPLAY_NAMES
            ]) ||
          member.role_name ||
          member.position ||
          "Executive Team";
        const execType = member.executive_type?.toLowerCase();

        return {
          id: member.user_id,
          name: member.full_name || member.email || "Unknown",
          title: member.position || "",
          roleLabel: displayRole,
          source: "executive" as const,
          sponsorType:
            execType === "primary" || execType === "secondary"
              ? (execType as "primary" | "secondary")
              : undefined,
        };
      })
      .filter(item => item.id);

    const merged: VendorStakeholder[] = [...accountStakeholders];
    const seen = new Set(accountStakeholders.map(item => item.id));

    executiveStakeholders.forEach(item => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      merged.push(item);
    });

    return merged;
  }, [
    accountTeam.members,
    executiveTeam.extraSponsors,
    executiveTeam.sponsors,
  ]);

  const mappedPairs = useMemo(() => {
    const seen = new Set<string>();
    stakeholderRelationships.items.forEach(item => {
      if (item.active === false) return;
      const customerId = item.customer_team_member;
      const vendorId =
        item.account_team_member ||
        item.executive_team_member ||
        (item as { vendor_stakeholder?: string }).vendor_stakeholder;
      if (!vendorId || !customerId) return;
      seen.add(`${vendorId}::${customerId}`);
    });
    return seen.size;
  }, [stakeholderRelationships.items]);

  const totalPairs = vendorStakeholders.length * customerTeam.members.length;

  const accountTeamCount = vendorStakeholders.filter(
    vendor => vendor.source === "account"
  ).length;
  const execTeamCount = vendorStakeholders.filter(
    vendor => vendor.source === "executive"
  ).length;

  const summaryStats = [
    {
      id: "mapping",
      label: "Mapping",
      value: `${mappedPairs} / ${totalPairs}`,
      description: `${mappedPairs} mapped pairs`,
    },
    {
      id: "account-team",
      label: "Account Team",
      value: String(accountTeamCount),
      description: "Vendor stakeholders",
    },
    {
      id: "exec-team",
      label: "Exec Team",
      value: String(execTeamCount),
      description: "Vendor stakeholders",
    },
    {
      id: "customer-contacts",
      label: "Customer Contacts",
      value: String(customerTeam.members.length),
      description: "Customer stakeholders",
    },
  ];

  const handleSaveStakeholderMappings = async (
    links: {
      team_type: "account_team_member" | "executive_team_member";
      account_team_member?: string;
      executive_team_member?: string;
      customer_team_member: string;
      relationship_percentage: number | null;
      is_priority: boolean;
    }[],
    transferDecisions: {
      relationship_id: string;
      successor_user_id?: string | null;
      leave_dormant?: boolean;
    }[] = []
  ) => {
    if (!accountId) return false;
    try {
      await dispatch(
        saveStakeholderRelationships({
          customerId: accountId,
          links,
          transferDecisions,
        })
      ).unwrap();
      dispatch(fetchStakeholderRelationships(accountId));
      toast.success("Stakeholder mapping updated successfully");
      return true;
    } catch (error: unknown) {
      const message =
        typeof error === "string"
          ? error
          : (error as { message?: string })?.message ||
            "Failed to update stakeholder mapping";
      toast.error(message);
      return false;
    }
  };

  const canOpenMatrix =
    vendorStakeholders.length > 0 && customerTeam.members.length > 0;

  return (
    <>
      <section
        className={`box-border flex min-h-[280px] w-full min-w-0 flex-col overflow-hidden rounded-lg border border-[#EBEBEB] bg-white px-4 pb-3 pt-3.5 ${className}`}
      >
        <div className="flex shrink-0 min-h-6 items-center justify-between gap-2">
          <h2 className="m-0 text-base font-bold leading-6 text-black">
            Stakeholder Mapping
          </h2>
          <button
            type="button"
            onClick={() => setIsMatrixModalOpen(true)}
            disabled={!canOpenMatrix && !stakeholderRelationships.loading}
            className="border-0 bg-transparent p-0 text-sm font-semibold leading-6 text-[#7C3AED] hover:underline disabled:cursor-not-allowed disabled:text-gray-300 disabled:no-underline"
          >
            Account Relationship
          </button>
        </div>

        <hr className="m-0 mb-3 mt-3 w-full shrink-0 border-0 border-t border-[#F6F6F6]" />

        <div className="flex min-h-0 flex-1 flex-col gap-2">
          {stakeholderRelationships.loading ? (
            <p className="m-0 flex flex-1 items-center justify-center text-sm text-[#9F9F9F]">
              Loading…
            </p>
          ) : stakeholderRelationships.error ? (
            <p className="m-0 text-sm text-[#E01E21]" role="alert">
              {stakeholderRelationships.error}
            </p>
          ) : !canOpenMatrix ? (
            <p className="m-0 flex flex-1 items-center justify-center text-center text-sm text-[#9F9F9F]">
              Add vendor and customer stakeholders to view the relationship
              matrix.
            </p>
          ) : (
            <div className="grid h-full min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-2.5">
              {summaryStats.map(stat => (
                <div key={stat.id} className={INNER_CARD_CLASS}>
                  <p className="m-0 text-xs font-medium text-[#959595]">
                    {stat.label}
                  </p>
                  <p className="m-0 mt-1 text-base font-semibold leading-5 text-gray-900">
                    {stat.value}
                  </p>
                  <p className="m-0 mt-1 text-xs font-medium leading-[15px] text-[#959595]">
                    {stat.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <CommonModal
        isOpen={isMatrixModalOpen}
        toggle={() => setIsMatrixModalOpen(false)}
        size="7xl"
        showHeader={false}
        className="max-h-[92vh]"
        contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
      >
        <RelationshipMatrix
          accountId={accountId}
          vendorStakeholders={vendorStakeholders}
          customerStakeholders={customerTeam.members}
          relationships={stakeholderRelationships.items}
          loading={stakeholderRelationships.loading}
          error={stakeholderRelationships.error}
          saving={stakeholderRelationships.saving}
          saveError={stakeholderRelationships.saveError}
          onSave={handleSaveStakeholderMappings}
          onClose={() => setIsMatrixModalOpen(false)}
        />
      </CommonModal>
    </>
  );
};

export default StakeholdersMappingCard;
